import { z } from 'zod';
import { baseComponentSchema, imageCropSchema } from '../component-base.schemas';

// Image Info component schemas
export const imageInfoItemSchema = z.object({
  id: z.string().uuid('Invalid image info item ID'),
  imageUrl: z.string().url('Invalid image URL').optional(),
  cloudfrontUrl: z.string().url('Invalid CloudFront URL').optional(),
  alt: z.string().max(200, 'Alt text must be less than 200 characters').optional(),
  subtitle: z.string().max(200, 'Subtitle must be less than 200 characters').optional(),
  description: z.string().max(1000, 'Description must be less than 1000 characters').optional(),
  order: z.number().int().min(0).default(0),
  // Image crop data
  crop: imageCropSchema,
  button: z
    .object({
      text: z.string().min(1, 'Button text is required').max(30, 'Button text too long'),
      url: z
        .string()
        .url('Invalid button URL')
        .or(z.string().regex(/^\//, 'URL must be absolute or start with /')),
      style: z.enum(['primary', 'secondary', 'outline', 'ghost']),
      target: z.enum(['_blank', '_self']).optional(),
    })
    .optional(),
});

export const imageInfoCustomColorsSchema = z
  .object({
    background: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    text: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    title: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    subtitle: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    imageBorder: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    buttonBg: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    buttonText: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
  })
  .optional();

export const imageInfoDataSchema = z.object({
  title: z.string().max(200, 'Title must be less than 200 characters').optional(),
  subtitle: z.string().max(500, 'Subtitle must be less than 500 characters').optional(),
  showTitle: z.boolean().default(true),
  showSubtitle: z.boolean().default(false),
  showImageBorder: z.boolean().default(true),
  items: z.array(imageInfoItemSchema).min(1, 'At least one image info item is required'),
  layout: z.enum(['horizontal', 'vertical', 'grid']).default('horizontal'),
  columns: z.number().int().min(1).max(6).default(3),
  imageShape: z.enum(['round', 'square']).default('round'),
  imageSize: z.enum(['small', 'medium', 'large']).default('medium'),
  spacing: z.enum(['compact', 'normal', 'spacious']).default('normal'),
  customColors: imageInfoCustomColorsSchema,
});

export const imageInfoComponentSchema = baseComponentSchema.extend({
  type: z.literal('image-info'),
  data: imageInfoDataSchema,
});

// Type exports
export type ImageInfoItem = z.infer<typeof imageInfoItemSchema>;
export type ImageInfoData = z.infer<typeof imageInfoDataSchema>;
export type ImageInfoComponent = z.infer<typeof imageInfoComponentSchema>;
